import { useQuery } from '@tanstack/react-query'
import { queryClient } from '@/lib/react-query'
import { useAuthStore } from '@/store/useAuthStore'
import { useTeamContext } from '@/hooks/useTeamContext'
import { useApiReady } from '@/hooks/useApiReady'

export const CURRENT_USER_CACHE_MS = 1000 * 60 * 5

export const currentUserKeys = {
  all: ['currentUser'] as const,
  detail: (userId: string | undefined) => [...currentUserKeys.all, userId] as const,
}

export function getCurrentUserQueryKey(
  userId: string | undefined,
  teamId: string | null | undefined
) {
  return [...currentUserKeys.detail(userId), teamId ?? null] as const
}

export interface CurrentUserData {
  user: {
    id: string
    email: string | null
    email_verified?: boolean
    role: string
    first_name: string | null
    last_name: string | null
    job_title?: string | null
    avatar?: string | null
    linkedin_url?: string | null
    is_active?: boolean
    is_verified?: boolean
    team_tags?: Array<{ id: string; name: string }>
    team_tag?: string | null
    /** ESN mode for team users (from session shell) */
    is_esn?: boolean
    auth_providers?: string[]
    has_password?: boolean
  }
  role: string
  permissions: unknown[]
}

/** Données déjà seedées par GET /auth/session (cache React Query). */
export function getSeededCurrentUser(
  userId: string | undefined,
  teamId: string | null | undefined
): CurrentUserData | undefined {
  if (!userId) return undefined
  return queryClient.getQueryData(getCurrentUserQueryKey(userId, teamId))
}

/**
 * Hook centralisé pour l'utilisateur connecté.
 * Source unique : cache seedé par GET /auth/session (plus de GET /auth/me).
 */
export function useCurrentUser() {
  const { user } = useAuthStore()
  const { activeTeamId } = useTeamContext()
  const apiReady = useApiReady()
  const queryKey = getCurrentUserQueryKey(user?.id, activeTeamId)
  const seeded = getSeededCurrentUser(user?.id, activeTeamId)
  const seededUpdatedAt = seeded
    ? queryClient.getQueryState(queryKey)?.dataUpdatedAt ?? Date.now()
    : undefined

  return useQuery({
    queryKey,
    queryFn: async (): Promise<CurrentUserData> => {
      const cached = getSeededCurrentUser(user?.id, activeTeamId)
      if (cached) {
        return cached
      }

      const { fetchAndHydrateAuthSession } = await import('@/lib/auth/hydrateFromAuthSession')
      const hydrated = await fetchAndHydrateAuthSession()
      const afterHydrate = getSeededCurrentUser(user?.id, activeTeamId)
      if (hydrated && afterHydrate) {
        return afterHydrate
      }

      throw new Error('CURRENT_USER_NOT_SEEDED')
    },
    enabled: apiReady && !!user?.id,
    initialData: seeded,
    initialDataUpdatedAt: seededUpdatedAt,
    refetchOnMount: false,
    staleTime: CURRENT_USER_CACHE_MS,
    retry: 1,
    refetchOnWindowFocus: false,
  })
}
